import React from 'react';
import { CardFormatType, CardSizeType, VodCardType } from '../../../../../types/decks';
import type { useActions } from '../../../../../hooks/useActions';
import { VodCardLandscape } from './VodCardLandscape';
import { VodCardMiniHero } from './VodCardMiniHero';

export type CardProps = {
  card: VodCardType;
  cardSize: CardSizeType;
  cardFormat: CardFormatType;
  actions: ReturnType<typeof useActions>;
};

export function VodCard(props: CardProps): React.ReactNode {
  switch (props.cardFormat) {
    case 'MINI_HERO':
      return <VodCardMiniHero {...props} />;
    case 'PORTRAIT':
    case 'LANDSCAPE':
      return <VodCardLandscape {...props} />;
    default: {
      const _exhaustive: never = props.cardFormat;
      void _exhaustive;
      return <VodCardLandscape {...props} />;
    }
  }
}

export function VodCardActionButton({
  card,
  actions,
  onShowActionsModal,
}: {
  card: VodCardType;
  actions: ReturnType<typeof useActions>;
  onShowActionsModal: () => void;
}): React.ReactNode {
  if (!card.actionButton) {
    return null;
  }
  const { actionButton } = card;
  const action = actionButton.action;
  const isShowActionsModal = action?.clientAction?.__typename === 'ShowActionsModalClientAction';

  return (
    <button
      type="button"
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        if (isShowActionsModal) {
          onShowActionsModal();
          return;
        }
        if (action) {
          actions(action);
        }
      }}
      style={{
        color: actionButton.textColor ?? undefined,
        background: actionButton.backgroundColor ?? undefined,
        opacity: (actionButton.backgroundOpacityInPct ?? 100) / 100,
      }}
      className="z-[3] h-8 px-3 flex rounded-full items-center justify-center text-marker-xs overflow-hidden"
      aria-label={actionButton.title ?? 'Actions'}
    >
      {actionButton.iconUrl && (
        <img
          src={actionButton.iconUrl}
          alt=""
          aria-hidden="true"
          className={`h-4 ${actionButton.title ? 'mr-2' : ''}`}
        />
      )}
      {actionButton.title && <span>{actionButton.title}</span>}
    </button>
  );
}

export function VodCardButton({
  card,
  active,
  variant = 'absolute',
}: {
  card: VodCardType;
  active?: boolean;
  variant?: 'absolute' | 'inline';
}): React.ReactNode {
  if (!card.button) {
    return null;
  }
  const { button } = card;

  const color = active && button.focusedTextColor ? button.focusedTextColor : button.textColor;

  const ICON_ONLY_BUTTON_SIZE = 24;

  const buttonOnly = !button.title?.trim() && !card.progress?.percentage;
  return (
    <div
      style={{
        color: color ?? undefined,
        width: buttonOnly ? ICON_ONLY_BUTTON_SIZE : undefined,
        zIndex: variant === 'absolute' ? 3 : undefined,
      }}
      className={
        variant === 'absolute'
          ? 'absolute h-6 px-3 flex left-3 bottom-3 rounded-full items-center justify-center text-marker-xs overflow-hidden'
          : 'relative h-6 px-3 flex rounded-full items-center justify-center text-marker-xs overflow-hidden'
      }
    >
      <VodCardButtonBody card={card} active={active} />
    </div>
  );
}

function VodCardButtonBody({
  card,
  active,
}: {
  card: VodCardType;
  active?: boolean;
}): React.ReactNode {
  if (!card.button) {
    return null;
  }
  const { button } = card;
  const color = active && button.focusedTextColor ? button.focusedTextColor : button.textColor;
  const buttonOnly = !button.title?.trim() && !card.progress?.percentage;
  return (
    <>
      <div
        style={{
          background:
            active && button.focusedBackgroundColor
              ? button.focusedBackgroundColor
              : (button.backgroundColor ?? undefined),
          opacity:
            active && button.focusedBackgroundOpacityInPct
              ? button.focusedBackgroundOpacityInPct / 100
              : (button.backgroundOpacityInPct ?? 100) / 100,
        }}
        className="absolute inset-0 z-[1]"
      />
      {button.iconUrl && (
        <img
          src={active && button.activeIconUrl ? button.activeIconUrl : button.iconUrl}
          alt=""
          aria-hidden="true"
          className={`z-[1] ${buttonOnly ? 'absolute' : 'relative mr-2'} h-3`}
        />
      )}
      {card.progress?.percentage && (
        <div
          className={`z-[1] h-1 w-4 relative rounded-full mr-2 overflow-hidden bg-opacity-white-400`}
        >
          <div
            style={{
              background: color ?? undefined,
              width: `${card.progress?.percentage}%`,
            }}
            className="h-2 absolute left-0 top-0"
          />
        </div>
      )}
      {button.title && <span style={{ zIndex: 1 }}>{button.title}</span>}
    </>
  );
}
